import React, {createRef, useState} from 'react';
import {useQueryClient} from "react-query";
import {useAuth0} from "@auth0/auth0-react";
import MaterialTable from "@material-table/core";
import {defaultComponents} from "../../../controls/Material-Table-Components";
import { TablePagination, Select, MenuItem, useTheme } from "@material-ui/core";
import { ValidatorColumn, ColumnWidth } from "../../../blocks/ColumnHelper";
import FuelMarketsValidator from "../validators/FuelMarketsValidator";
import FuelMarketRateList from "./FuelMarketRateList";
import { CurrencyValue } from "../../../controls";

import { useFuelMarkets, useAddFuelMarket, useUpdateFuelMarket, useDeleteFuelMarket } from "../../../services/fuel";
import { 
  MaskedText,
} from "../../../controls";


const FuelMarketList = (_props) => {
  const theme = useTheme();
  const {isAuthenticated} = useAuth0();
  const validatorColumn = ValidatorColumn(FuelMarketsValidator, true);

  const props = {
    ..._props
  }
  const [state] = useState({
    canEdit: props.canEdit,
    activeRow: null
  });

  const tableRef = createRef({state: {lastEditingRow: false}});

  const handleAddFuelMarket = useAddFuelMarket();
  const handleUpdateFuelMarket = useUpdateFuelMarket();
  const handleDeleteFuelMarket = useDeleteFuelMarket();

  const queryClient = useQueryClient();
  const extraKeys = [];

  const fuelMarkets = useFuelMarkets({tableRef, queryClient, extraKeys});

  const handleRateChange = (changedFields, rowData) => {
    queryClient.setQueryData(['FuelMarkets'], (records) => {
      return records.map(fuelMarketRecord => {
        if (fuelMarketRecord.fuelMarketUuid === rowData.fuelMarketUuid) {
          return { ...fuelMarketRecord, ...changedFields };
        }
        return fuelMarketRecord;
      });
    });
  }

  const getColumns = (props) => { 
    const baseColumn = {
      cellStyle: { fontSize:14, padding: "8px 2px" },
      headerStyle: { padding: "8px 2px" },
      editable: props.canEdit ? "always" : "never",
      emptyValue: "",
      initialEditValue: "",
    };
    const formatChars={ 
      "U": "[A-Z]", 
      "A": "[A-Z0-9]", 
      "a": "[A-Za-z0-9]",
      "i": "[A-Za-z0-9\\-]",
      "9": "[0-9]",
      "D": "[0-9.]",
      "-": "-",
      "x": "."
    }
    const defaultEditProps = {
      style:{fontSize: "0.875rem"}
    };

    return [
      {...validatorColumn},
      /*
        Can use ...ColumnWidth(70) to generate a fixed width column
      */

      {
        ...baseColumn,
        ...(ColumnWidth(200)),
        title: "Fuel Market Code",
        field: "fuelMarketCode",
        initialEditValue: "",
        editComponent: (props) => (
          <MaskedText
            mask={"x".repeat(30)}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
            forceUpperCase={true}
          />
        )
      },
      {
        ...baseColumn,
        title: "Fuel Market Name",
        field: "fuelMarketName",
        initialEditValue: "",
        editComponent: (props) => (
          <MaskedText
            style={{width: 400, minWidth:200}}
            mask={"x".repeat(100)}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        )
      },
      {
        ...baseColumn,
        ...(ColumnWidth(120)),
        title: "Latest Date",
        field: "validFrom",
        initialEditValue: "",
        editable: "never",
        type: "date",
        sorting: false,
      },
      {
        ...baseColumn,
        ...(ColumnWidth(140)),
        title: "Latest Platts Price",
        field: "dollarPlattsPrice",
        initialEditValue: "",
        editable: "never",
        type: "numeric",
        sorting: false,
        render: (rowData) => (
          <CurrencyValue
            decimalScale={3}
            fixedDecimalScale={true}
            currencyCode="USDC"
            value={rowData.dollarPlattsPrice}
          />
        )
      },
      {
        ...baseColumn,
        ...(ColumnWidth(200, undefined, undefined, {paddingLeft: 12}, {paddingLeft: 12})),
        title: "Fuel Market Type",
        field: "fuelMarketType",
        editable: state.canEdit ? "always" : "never",
        emptyValue: "",
        initialEditValue: "",
        editComponent: (props) => {
          const comboValues = [{"code":"MARKET","value":"Market"},{"code":"REFINERY","value":"Refinery"}];
          return (
            <Select
              style={{width: 180}}
              value={props.value == null?'':props.value}
              onChange={
                (evt, data) => {
                  props.onChange(data.props.value);
                }
              }
              renderValue={(value) => `${value}`}
            >
              {(comboValues) ? 
                comboValues.map((record) => {
                    return (
                      <MenuItem key={record.code} value={record.code}>{record.code} - {record.value}</MenuItem>
                    );
                }) :
                null
              }
            </Select>
          )
        }
      },
    ];
  };
  
  return (isAuthenticated &&
      <div style={{width: "100%", marginLeft: 20, marginRight: 20}}>
        <MaterialTable
            tableRef={tableRef}
            style={{
              border: "none",
              boxShadow: "none",
              borderRadius: 0,
            }}
            options={{
              actionsColumnIndex: -1,
              cellStyle: {
                padding: "8px 2px",
              },
              headerStyle: {
                backgroundColor: theme.palette.tableHeader.backgroundColor,
                color: theme.palette.tableHeader.textColor,
                padding: "2px",
              },
              toolbarButtonAlignment: "right",
              pageSize: 10,
              detailPanelType: "single",
            }}
            components={{
              ...defaultComponents,
              Pagination: (props) => (
                  <TablePagination
                      {...props}
                      rowsPerPageOptions={[5, 10, 15]}
                  />
              ),
            }}
            title="Fuel Market Management"
            columns={getColumns({canEdit: state.canEdit})}
            detailPanel={
              [
                {
                  tooltip: "Show Rates",
                  render: (rowData) => {
                    return (
                      <div>
                        <div style={{ backgroundColor: "#f2f2f2" }}>
                          <FuelMarketRateList
                            canEdit={state.canEdit}
                            record={rowData}
                            onChange={(changedData) => {handleRateChange(changedData, rowData)}}
                          />
                        </div>
                      </div>
                    );
                  },
                },
              ]
            }
            data={fuelMarkets.data}
            editable={{
              isEditable: () => {
                return state.canEdit;
              },
              isDeletable: () => {
                return state.canEdit;
              },
              onRowAdd: state.canEdit
                  ? (newData) => new Promise((resolve, reject) => {
                    handleAddFuelMarket.mutate({record: newData, resolve, reject, extraKeys});
                  })
                  : undefined,
              onRowUpdate: (newData, oldData) =>
                  new Promise((resolve, reject) => {
                    handleUpdateFuelMarket.mutate({record: newData, oldRecord: oldData, resolve, reject, extraKeys});
                  }),
              onRowDelete: (oldData) =>
                  new Promise((resolve, reject) => {
                    handleDeleteFuelMarket.mutate({oldRecord: oldData, resolve, reject, extraKeys});
                  }),
            }}
        />
      </div>
  );

}

export default FuelMarketList;
